import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Deduplication Rule Types ---

export interface DeduplicationMatchField {
  field_name: string;
  match_type: string;
  weight: number;
  case_sensitive: boolean;
}

export interface DeduplicationRule {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  target_module: string;
  match_fields: DeduplicationMatchField[];
  match_strategy: string;
  similarity_threshold: number;
  merge_strategy: string;
  is_active: boolean;
  auto_merge: boolean;
  last_run_at: string | null;
  total_duplicates_found: number;
  total_merged: number;
  owner_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface CreateDeduplicationRuleRequest {
  name: string;
  description?: string;
  target_module: string;
  match_fields: DeduplicationMatchField[];
  match_strategy?: string;
  similarity_threshold?: number;
  merge_strategy?: string;
  is_active?: boolean;
  auto_merge?: boolean;
}

export interface UpdateDeduplicationRuleRequest {
  name?: string;
  description?: string;
  match_fields?: DeduplicationMatchField[];
  match_strategy?: string;
  similarity_threshold?: number;
  merge_strategy?: string;
  is_active?: boolean;
  auto_merge?: boolean;
}

// --- Deduplication Job Types ---

export interface DuplicateGroup {
  group_id: string;
  record_ids: string[];
  similarity_score: number;
  match_fields: string[];
  master_record_id: string | null;
  status: string;
}

export interface DeduplicationJob {
  id: string;
  tenant_id: string;
  rule_id: string;
  rule_name: string;
  target_module: string;
  status: string;
  total_scanned: number;
  duplicate_groups_found: number;
  records_merged: number;
  records_skipped: number;
  duplicate_groups: DuplicateGroup[];
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  owner_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

export interface MergeDuplicatesRequest {
  group_id: string;
  master_record_id: string;
}

// --- Constants ---

export const DeduplicationMatchStrategies = ['all_fields', 'any_field', 'weighted_score'] as const;
export const DeduplicationMergeStrategies = ['keep_newest', 'keep_oldest', 'keep_most_complete', 'manual'] as const;
export const DeduplicationMatchTypes = ['exact', 'fuzzy', 'phonetic', 'normalized'] as const;
export const DeduplicationJobStatuses = ['pending', 'scanning', 'review', 'merging', 'completed', 'failed', 'cancelled'] as const;
export const DuplicateGroupStatuses = ['pending', 'merged', 'skipped', 'rejected'] as const;

// --- Deduplication Rule API ---

export const listDeduplicationRules = (params: SearchParams & {
  target_module?: string;
  is_active?: boolean;
  owner_id?: string;
}) =>
  apiGet<ApiResponse<PagedResponse<DeduplicationRule>>>('/deduplication-rules', params);

export const getDeduplicationRule = (id: string) =>
  apiGet<ApiResponse<DeduplicationRule>>(`/deduplication-rules/${id}`);

export const createDeduplicationRule = (data: CreateDeduplicationRuleRequest) =>
  apiPost<ApiResponse<DeduplicationRule>>('/deduplication-rules', data);

export const updateDeduplicationRule = (id: string, data: UpdateDeduplicationRuleRequest) =>
  apiPut<ApiResponse<DeduplicationRule>>(`/deduplication-rules/${id}`, data);

export const deleteDeduplicationRule = (id: string) =>
  apiDelete<ApiResponse<object>>(`/deduplication-rules/${id}`);

export const getDeduplicationMatchFields = (module: string) =>
  apiGet<ApiResponse<string[]>>(`/deduplication-rules/match-fields/${module}`);

// --- Deduplication Job API ---

export const listDeduplicationJobs = (params: SearchParams & {
  status?: string;
  rule_id?: string;
  target_module?: string;
  owner_id?: string;
}) =>
  apiGet<ApiResponse<PagedResponse<DeduplicationJob>>>('/deduplication-jobs', params);

export const getDeduplicationJob = (id: string) =>
  apiGet<ApiResponse<DeduplicationJob>>(`/deduplication-jobs/${id}`);

export const runDeduplicationJob = (ruleId: string) =>
  apiPost<ApiResponse<DeduplicationJob>>(`/deduplication-jobs/run/${ruleId}`);

export const mergeDeduplicationGroup = (jobId: string, data: MergeDuplicatesRequest) =>
  apiPost<ApiResponse<DeduplicationJob>>(`/deduplication-jobs/${jobId}/merge`, data);

export const skipDeduplicationGroup = (jobId: string, groupId: string) =>
  apiPost<ApiResponse<DeduplicationJob>>(`/deduplication-jobs/${jobId}/skip/${groupId}`);

export const cancelDeduplicationJob = (id: string) =>
  apiPost<ApiResponse<DeduplicationJob>>(`/deduplication-jobs/${id}/cancel`);

export const deleteDeduplicationJob = (id: string) =>
  apiDelete<ApiResponse<object>>(`/deduplication-jobs/${id}`);
